const UII_BASE = import.meta.env.VITE_UII_BASE_URL || 'https://admin.adnuntius.com'

interface RouteMapping {
  pattern: RegExp
  destination: string
  newDestination?: string
}

const ROUTE_MAPPINGS: RouteMapping[] = [
  {
    pattern: /^\/advertising\/advertisers(?:\/([^/]+))?\/?$/,
    destination: '/advertisers/advertiser/$1',
    newDestination: '/advertisers/new',
  },
  {
    pattern: /^\/advertising\/orders(?:\/([^/]+))?\/?$/,
    destination: '/orders/order/$1',
    newDestination: '/orders/new',
  },
  {
    pattern: /^\/advertising\/lineitems(?:\/([^/]+))?\/?$/,
    destination: '/line-items/line-item/$1',
    newDestination: '/line-items/new',
  },
  {
    // Before the creative mapping, which would read "upload" as a creative id.
    pattern: /^\/advertising\/creatives\/upload\/?$/,
    destination: '/creatives/bulk-upload',
  },
  {
    pattern: /^\/advertising\/creatives(?:\/([^/]+))?\/?$/,
    destination: '/creatives/creative/$1',
    newDestination: '/creatives/new',
  },
  {
    pattern: /^\/publishing\/earnings(?:\/([^/]+))?\/?$/,
    destination: '/earnings-accounts/earnings-account/$1',
    newDestination: '/earnings-accounts/new',
  },
  {
    pattern: /^\/publishing\/sites(?:\/([^/]+))?\/?$/,
    destination: '/sites/site/$1',
    newDestination: '/sites/new',
  },
  {
    pattern: /^\/publishing\/site-groups(?:\/([^/]+))?\/?$/,
    destination: '/site-groups/site-group/$1',
    newDestination: '/site-groups/new',
  },
  {
    pattern: /^\/publishing\/ad-units(?:\/([^/]+))?\/?$/,
    destination: '/ad-units/ad-unit/$1',
    newDestination: '/ad-units/new',
  },
  {
    pattern: /^\/publishing\/blocklists(?:\/([^/]+))?\/?$/,
    destination: '/blocklists/blocklist/$1',
    newDestination: '/blocklists/new',
  },
  {
    pattern: /^\/publishing\/rulesets(?:\/([^/]+))?\/?$/,
    destination: '/rulesets/ruleset/$1',
    newDestination: '/rulesets/new',
  },
  {
    pattern: /^\/admin\/layouts(?:\/([^/]+))?\/?$/,
    destination: '/admin/layouts/layout/$1',
    newDestination: '/admin/layouts/new',
  },
  {
    pattern: /^\/admin\/users(?:\/([^/]+))?\/?$/,
    destination: '/admin/users/user/$1',
    newDestination: '/admin/users/new',
  },
  {
    pattern: /^\/admin\/products(?:\/([^/]+))?\/?$/,
    destination: '/admin/products/product/$1',
    newDestination: '/admin/products/new',
  },
  {
    pattern: /^\/admin\/teams(?:\/([^/]+))?\/?$/,
    destination: '/admin/teams/team/$1',
    newDestination: '/admin/teams/new',
  },
  {
    pattern: /^\/admin\/team-groups(?:\/([^/]+))?\/?$/,
    destination: '/admin/team-groups/team-group/$1',
    newDestination: '/admin/team-groups/new',
  },
  {
    pattern: /^\/admin\/custom-events(?:\/([^/]+))?\/?$/,
    destination: '/admin/custom-events/custom-event/$1',
    newDestination: '/admin/custom-events/new',
  },
  {
    pattern: /^\/admin\/tiers(?:\/([^/]+))?\/?$/,
    destination: '/admin/tiers/tier/$1',
    newDestination: '/admin/tiers/new',
  },
  {
    pattern: /^\/admin\/data-exports(?:\/([^/]+))?\/?$/,
    destination: '/admin/data-exports/data-export/$1',
    newDestination: '/admin/data-exports/new',
  },
  {
    pattern: /^\/reports\/templates(?:\/([^/]+))?\/?$/,
    destination: '/report-templates/report-template/$1',
    newDestination: '/report-templates/new',
  },
  {
    pattern: /^\/reports\/schedules(?:\/([^/]+))?\/?$/,
    destination: '/report-schedules/report-schedule/$1',
    newDestination: '/report-schedules/new',
  },
  {
    pattern: /^\/reports\/translations(?:\/([^/]+))?\/?$/,
    destination: '/report-translations/report-translation/$1',
    newDestination: '/report-translations/new',
  },
  {
    pattern: /^\/political\/entities(?:\/([^/]+))?\/?$/,
    destination: '/political-entities/political-entity/$1',
    newDestination: '/political-entities/new',
  },
  {
    pattern: /^\/political\/campaigns(?:\/([^/]+))?\/?$/,
    destination: '/political-campaigns/political-campaign/$1',
    newDestination: '/political-campaigns/new',
  },
  {
    pattern: /^\/political\/users(?:\/([^/]+))?\/?$/,
    destination: '/admin/users/user/$1',
    newDestination: '/admin/users/new',
  },
]

function mappedPath(pathname: string, search: string): string | undefined {
  const isNew = new URLSearchParams(search).get('new') === 'true'
  for (const mapping of ROUTE_MAPPINGS) {
    const match = pathname.match(mapping.pattern)
    if (match) {
      if (isNew && mapping.newDestination) return mapping.newDestination
      return mapping.destination.replace('/$1', match[1] ? '/' + match[1] : '')
    }
  }
}

function moduleHome(sourceModule: string): string {
  if (sourceModule === 'admin') return '/admin'
  if (sourceModule === 'political') return '/political-entities'
  if (sourceModule === 'publishing') return '/earnings-accounts'
  return '/advertisers'
}

export function translateRuiPathToUii(
  pathname: string,
  search: string,
  sourceModule: string
): string {
  const translated = mappedPath(pathname, search)
  if (translated) return translated
  if (pathname === '/admin/networks') return '/admin/network'
  if (pathname === '/preferences') return '/admin/user-profile'
  if (pathname === '/admin/notification-preferences') return '/admin/notification-preferences'
  if (pathname === '/ai') return '/ai'
  if (pathname === '/advertising/reports' || pathname === '/publishing/reports') return '/reports'
  if (pathname === '/political/elections' || pathname.startsWith('/political/elections/'))
    return '/elections'
  if (pathname.startsWith('/admin') || pathname.startsWith('/super-admin')) return '/admin'
  if (pathname.startsWith('/reports')) return '/reports'
  if (pathname.startsWith('/political')) return '/home'
  if (pathname.startsWith('/publishing')) return '/earnings-accounts'
  if (pathname.startsWith('/advertising')) return '/advertisers'
  return moduleHome(sourceModule)
}

export function uiiUrlForLocation(pathname: string, search: string, sourceModule: string): string {
  const url = new URL(translateRuiPathToUii(pathname, search, sourceModule), UII_BASE)
  url.searchParams.set('source', 'rui')
  return url.toString()
}
